import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Mail, Send, Eye, Edit3, Check, X, Clock, Loader2,
  FileText, Users, History, ChevronDown, ChevronUp, Search,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Category color map
const CATEGORY_COLORS = {
  invitation: "bg-blue-100 text-blue-700 border-blue-200",
  completion: "bg-green-100 text-green-700 border-green-200",
  scoring: "bg-purple-100 text-purple-700 border-purple-200",
  gate: "bg-amber-100 text-amber-700 border-amber-200",
  reminder: "bg-orange-100 text-orange-700 border-orange-200",
};

export default function TAEmailTab({ token }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyTotal, setHistoryTotal] = useState(0);

  // Compose state
  const [composing, setComposing] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [editSubject, setEditSubject] = useState("");
  const [editBody, setEditBody] = useState("");

  // Preview state
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Candidate selection for sending
  const [candidates, setCandidates] = useState([]);
  const [candidateSearch, setCandidateSearch] = useState("");
  const [selectedCandidates, setSelectedCandidates] = useState([]);
  const [sending, setSending] = useState(false);

  // Sub-view: templates | compose | history
  const [subView, setSubView] = useState("templates");

  const authHeaders = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [tplRes, statsRes, histRes, candRes] = await Promise.all([
        fetch(`${API}/ta/email/templates`),
        fetch(`${API}/ta/email/stats`),
        fetch(`${API}/ta/email/history?limit=20`),
        fetch(`${API}/ta/scoring/queue`),
      ]);
      const tplData = await tplRes.json();
      const statsData = await statsRes.json();
      const histData = await histRes.json();
      const candData = await candRes.json();

      setTemplates(tplData.templates || []);
      setStats(statsData);
      setHistory(histData.history || []);
      setHistoryTotal(histData.total || 0);
      setCandidates(candData.candidates || []);
    } catch {
      toast.error("Failed to load email data");
    }
    setLoading(false);
  };

  const handlePreview = async (template, candidateId = null) => {
    setPreviewLoading(true);
    try {
      const body = { template_id: template.id };
      if (candidateId) body.candidate_id = candidateId;
      const res = await fetch(`${API}/ta/email/preview`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      setPreviewData({ ...data, template_name: template.name });
    } catch {
      toast.error("Preview failed");
    }
    setPreviewLoading(false);
  };

  const handleEditSave = async () => {
    if (!editingTemplate) return;
    try {
      const res = await fetch(`${API}/ta/email/templates/${editingTemplate.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...authHeaders },
        body: JSON.stringify({ subject: editSubject, body: editBody }),
      });
      if (res.ok) {
        toast.success("Template updated");
        setEditingTemplate(null);
        fetchAll();
      } else {
        toast.error("Failed to update template");
      }
    } catch {
      toast.error("Update failed");
    }
  };

  const handleSend = async () => {
    if (!selectedTemplate || selectedCandidates.length === 0) return;
    setSending(true);
    try {
      const res = await fetch(`${API}/ta/email/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders },
        body: JSON.stringify({
          template_id: selectedTemplate.id,
          candidate_ids: selectedCandidates,
        }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(`${data.emails_sent} email(s) queued (mock)`);
        setSelectedCandidates([]);
        setComposing(false);
        setSubView("history");
        fetchAll();
      } else {
        toast.error(data.detail || "Send failed");
      }
    } catch {
      toast.error("Send failed");
    }
    setSending(false);
  };

  const toggleCandidate = (id) => {
    setSelectedCandidates((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]
    );
  };

  const selectAll = () => {
    const filtered = filteredCandidates.map((c) => c.candidate_id);
    setSelectedCandidates(filtered);
  };

  const filteredCandidates = candidates.filter((c) => {
    const q = candidateSearch.toLowerCase();
    return !q || c.full_name?.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q);
  });

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[#00AEEF]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="ta-email-tab">
      {/* Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-4 pb-3 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-50">
              <FileText className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#3b3b3b]">{templates.length}</p>
              <p className="text-xs text-muted-foreground">Email Templates</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-green-50">
              <Send className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#3b3b3b]" data-testid="ta-email-sent-count">
                {stats?.total_emails_sent || 0}
              </p>
              <p className="text-xs text-muted-foreground">Emails Sent (Mock)</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-purple-50">
              <Users className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[#3b3b3b]">{stats?.total_ta_candidates || 0}</p>
              <p className="text-xs text-muted-foreground">TA/HOS Candidates</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Sub-navigation */}
      <div className="flex gap-2">
        {[
          { key: "templates", label: "Templates", icon: FileText },
          { key: "compose", label: "Compose & Send", icon: Send },
          { key: "history", label: "Send History", icon: History },
        ].map(({ key, label, icon: Icon }) => (
          <Button
            key={key}
            variant={subView === key ? "default" : "outline"}
            size="sm"
            onClick={() => setSubView(key)}
            className={subView === key ? "bg-[#00AEEF] text-white" : "text-[#00AEEF] border-[#00AEEF]/30"}
            data-testid={`ta-email-tab-${key}`}
          >
            <Icon className="w-4 h-4 mr-1.5" /> {label}
          </Button>
        ))}
      </div>

      {/* ===== Templates View ===== */}
      {subView === "templates" && (
        <Card>
          <CardHeader>
            <CardTitle className="text-[#00AEEF]">TA/HOS Email Templates</CardTitle>
            <CardDescription>
              {templates.length} templates available. Click Preview to see rendered output, or Edit to customize.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table data-testid="ta-email-templates-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Template</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Variables</TableHead>
                  <TableHead>Last Used</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {templates.map((tpl) => (
                  <TableRow key={tpl.id}>
                    <TableCell>
                      <div className="font-medium text-[#3b3b3b]">{tpl.name}</div>
                      <div className="text-xs text-muted-foreground font-mono">{tpl.slug}</div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={CATEGORY_COLORS[tpl.category] || "bg-gray-100"}>
                        {tpl.category}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {(tpl.variables || []).slice(0, 3).map((v) => (
                          <Badge key={v} variant="secondary" className="text-[10px] font-mono">
                            {`{{${v}}}`}
                          </Badge>
                        ))}
                        {(tpl.variables || []).length > 3 && (
                          <Badge variant="secondary" className="text-[10px]">
                            +{tpl.variables.length - 3}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-muted-foreground">
                        {tpl.last_used_at ? new Date(tpl.last_used_at).toLocaleDateString() : "Never"}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-1.5 justify-end">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handlePreview(tpl)}
                          className="text-[#00AEEF] border-[#00AEEF]/30 hover:bg-[#00AEEF]/10"
                          data-testid={`preview-${tpl.slug}`}
                        >
                          <Eye className="w-3.5 h-3.5 mr-1" /> Preview
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setEditingTemplate(tpl);
                            setEditSubject(tpl.subject);
                            setEditBody(tpl.body);
                          }}
                          className="text-amber-600 border-amber-200 hover:bg-amber-50"
                          data-testid={`edit-${tpl.slug}`}
                        >
                          <Edit3 className="w-3.5 h-3.5 mr-1" /> Edit
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* ===== Compose & Send View ===== */}
      {subView === "compose" && (
        <div className="space-y-4">
          {/* Step 1: Select Template */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-[#00AEEF] text-base">Step 1: Select Template</CardTitle>
            </CardHeader>
            <CardContent>
              <Select
                value={selectedTemplate?.id || ""}
                onValueChange={(val) => {
                  const tpl = templates.find((t) => t.id === val);
                  setSelectedTemplate(tpl);
                  if (tpl) handlePreview(tpl);
                }}
              >
                <SelectTrigger data-testid="compose-template-select">
                  <SelectValue placeholder="Choose an email template..." />
                </SelectTrigger>
                <SelectContent>
                  {templates.map((tpl) => (
                    <SelectItem key={tpl.id} value={tpl.id}>
                      {tpl.name} ({tpl.category})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </CardContent>
          </Card>

          {/* Step 2: Select Recipients */}
          {selectedTemplate && (
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-[#00AEEF] text-base">
                    Step 2: Select Recipients ({selectedCandidates.length} selected)
                  </CardTitle>
                  <div className="flex gap-2">
                    <Button variant="ghost" size="sm" onClick={selectAll} data-testid="compose-select-all">
                      Select All
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setSelectedCandidates([])}>
                      Clear
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="mb-3">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Search candidates..."
                      value={candidateSearch}
                      onChange={(e) => setCandidateSearch(e.target.value)}
                      className="pl-9 h-9"
                      data-testid="compose-candidate-search"
                    />
                  </div>
                </div>
                <div className="max-h-60 overflow-y-auto border rounded-lg">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-10"></TableHead>
                        <TableHead>Candidate</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredCandidates.map((c) => (
                        <TableRow
                          key={c.candidate_id}
                          className="cursor-pointer hover:bg-[#00AEEF]/5"
                          onClick={() => toggleCandidate(c.candidate_id)}
                        >
                          <TableCell>
                            <input
                              type="checkbox"
                              checked={selectedCandidates.includes(c.candidate_id)}
                              readOnly
                              className="w-4 h-4 accent-[#00AEEF]"
                              data-testid={`compose-check-${c.candidate_id}`}
                            />
                          </TableCell>
                          <TableCell>
                            <div className="font-medium text-sm">{c.full_name}</div>
                            <div className="text-xs text-muted-foreground">{c.email}</div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className={
                              c.assessment_type === "technical_assessment"
                                ? "bg-blue-50 text-blue-700 border-blue-200"
                                : "bg-purple-50 text-purple-700 border-purple-200"
                            }>
                              {c.assessment_type === "technical_assessment" ? "TA" : "HOS"}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className={
                              c.status === "completed" ? "bg-green-50 text-green-700" :
                              c.status === "started" ? "bg-yellow-50 text-yellow-700" :
                              "bg-gray-50 text-gray-600"
                            }>
                              {c.status || "pending"}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                      {filteredCandidates.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={4} className="text-center text-muted-foreground py-6">
                            No TA/HOS candidates found
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 3: Preview & Send */}
          {selectedTemplate && selectedCandidates.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-[#00AEEF] text-base">Step 3: Preview & Send</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {previewData && (
                  <div className="border rounded-lg p-4 bg-gray-50/50">
                    <div className="flex items-center gap-2 mb-2">
                      <Mail className="w-4 h-4 text-[#00AEEF]" />
                      <span className="font-medium text-sm">{previewData.subject}</span>
                    </div>
                    <pre className="whitespace-pre-wrap text-sm text-[#3b3b3b] font-sans leading-relaxed">
                      {previewData.body}
                    </pre>
                    <p className="text-xs text-muted-foreground mt-3 italic">
                      Variables shown as [placeholders] will be replaced with real candidate data on send.
                    </p>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <div className="text-sm text-muted-foreground">
                    Sending to <strong>{selectedCandidates.length}</strong> recipient(s) using <strong>{selectedTemplate.name}</strong>
                  </div>
                  <Button
                    onClick={handleSend}
                    disabled={sending}
                    className="bg-[#00AEEF] hover:bg-[#0098d0] text-white gap-2"
                    data-testid="compose-send-btn"
                  >
                    {sending ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /> Sending...</>
                    ) : (
                      <><Send className="w-4 h-4" /> Send Email (Mock)</>
                    )}
                  </Button>
                </div>
                <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded p-2">
                  Note: Emails are recorded but NOT delivered. Connect a real email provider (SendGrid, Resend) to enable actual delivery.
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* ===== History View ===== */}
      {subView === "history" && (
        <Card>
          <CardHeader>
            <CardTitle className="text-[#00AEEF]">Email Send History</CardTitle>
            <CardDescription>{historyTotal} total emails recorded</CardDescription>
          </CardHeader>
          <CardContent>
            {history.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Mail className="w-12 h-12 mx-auto mb-3 opacity-40" />
                <p>No emails sent yet</p>
                <p className="text-sm">Use the Compose & Send tab to send your first TA/HOS email</p>
              </div>
            ) : (
              <Table data-testid="ta-email-history-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Subject</TableHead>
                    <TableHead>Recipient</TableHead>
                    <TableHead>Template</TableHead>
                    <TableHead>Sent By</TableHead>
                    <TableHead>Sent At</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {history.map((h) => (
                    <TableRow key={h.id}>
                      <TableCell className="max-w-[200px] truncate font-medium text-sm">
                        {h.subject}
                      </TableCell>
                      <TableCell className="text-sm">{h.recipient_email}</TableCell>
                      <TableCell>
                        <Badge variant="secondary" className="text-xs font-mono">
                          {h.template_used}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{h.sent_by}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {h.sent_at ? new Date(h.sent_at).toLocaleString() : "—"}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={
                          h.status === "mock_sent" ? "bg-amber-50 text-amber-700 border-amber-200" :
                          h.status === "sent" ? "bg-green-50 text-green-700 border-green-200" :
                          "bg-gray-50 text-gray-600"
                        }>
                          {h.status}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {/* ===== Edit Template Modal ===== */}
      {editingTemplate && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="edit-template-modal">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-y-auto">
            <div className="p-6 border-b flex items-center justify-between">
              <h3 className="text-lg font-semibold text-[#3b3b3b]">Edit Template: {editingTemplate.name}</h3>
              <Button variant="ghost" size="sm" onClick={() => setEditingTemplate(null)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="p-6 space-y-4">
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Subject Line</label>
                <Input
                  value={editSubject}
                  onChange={(e) => setEditSubject(e.target.value)}
                  data-testid="edit-template-subject"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Email Body</label>
                <Textarea
                  value={editBody}
                  onChange={(e) => setEditBody(e.target.value)}
                  rows={14}
                  className="font-mono text-sm"
                  data-testid="edit-template-body"
                />
              </div>
              <div className="text-xs text-muted-foreground">
                Available variables: {(editingTemplate.variables || []).map((v) => `{{${v}}}`).join(", ")}
              </div>
            </div>
            <div className="p-6 border-t flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEditingTemplate(null)}>Cancel</Button>
              <Button onClick={handleEditSave} className="bg-[#00AEEF] text-white" data-testid="edit-template-save">
                <Check className="w-4 h-4 mr-1.5" /> Save Changes
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ===== Preview Modal ===== */}
      {previewData && subView === "templates" && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="preview-modal">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-y-auto">
            <div className="p-6 border-b flex items-center justify-between">
              <h3 className="text-lg font-semibold text-[#3b3b3b]">
                Preview: {previewData.template_name}
              </h3>
              <Button variant="ghost" size="sm" onClick={() => setPreviewData(null)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="p-6">
              <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2 text-sm">
                  <span className="font-medium text-muted-foreground">Subject:</span>
                  <span className="text-[#3b3b3b]">{previewData.subject}</span>
                </div>
              </div>
              <div className="border rounded-lg p-4">
                <pre className="whitespace-pre-wrap text-sm text-[#3b3b3b] font-sans leading-relaxed">
                  {previewData.body}
                </pre>
              </div>
              <p className="text-xs text-muted-foreground mt-3">
                Placeholders like [full_name] will be replaced with real candidate data when sent.
              </p>
            </div>
            <div className="p-6 border-t flex justify-end">
              <Button variant="outline" onClick={() => setPreviewData(null)}>Close</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
